'use client';
import { useEffect } from 'react';
import Image from 'next/image';
import HeroCarousel from './components/HeroCarousel';
import Pricing from './components/Pricing';
import Footer from './components/Footer';
import HowItWorks from './components/HowItWorks';
import ValueBenefits from './components/ValueBenefits';
import UseCases from './components/UseCases';
import TrustSection from './components/TrustSection';
import SalesCopy from './components/SalesCopy';

export default function Homepage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <nav className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image
              src="/assets/images/ChatGPT_Image_13._feb._2026_10.17.16-1771010403596.png"
              alt="Dataflowsuite Logo"
              width={40}
              height={40}
              className="object-contain"
            />
            <span className="text-xl font-serif tracking-tight text-foreground">Dataflowsuite</span>
          </div>

          <div className="flex items-center gap-6">
            <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden md:block">
              Sådan virker det
            </a>
            <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden md:block">
              Priser
            </a>
            <a href="#contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden md:block">
              Kontakt
            </a>
            <a
              href="/portal/login"
              className="px-6 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Portal Login
            </a>
          </div>
        </nav>
      </header>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 border-b border-border overflow-hidden">
        <HeroCarousel />

        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-8 flex flex-col justify-center">
              <span className="inline-block px-4 py-2 mb-8 border border-border bg-card/50 backdrop-blur-md rounded-full text-xs uppercase tracking-widest text-muted-foreground w-fit reveal">
                <span className="w-2 h-2 rounded-full bg-success inline-block mr-2 animate-pulse" />
                API Online
              </span>

              <h1 className="text-6xl md:text-8xl lg:text-9xl font-light text-foreground leading-none tracking-tight italic mb-8 reveal delay-100">
                <span className="block font-serif">Automatisér</span>
                <span className="block text-muted-foreground font-serif">dine dokumenter</span>
                <span className="block text-primary font-serif">– direkte ind</span>
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-4 leading-relaxed reveal delay-200">
                Stop med manuelt arbejde i PDF og Excel. Dataflowsuite konverterer dine dokumenter til struktureret data og validerer det automatisk i dit ERP og andre systemer.
              </p>
              <p className="text-base text-muted-foreground/70 max-w-xl mb-12 reveal delay-200">
                → Spar tid, reducer fejl og få fuldt overblik
              </p>

              <div className="flex flex-col sm:flex-row gap-4 reveal delay-300">
                <a
                  href="#contact"
                  className="px-8 py-4 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity text-center"
                >
                  Book demo
                </a>
                <a
                  href="#how-it-works"
                  className="px-8 py-4 border border-border text-foreground rounded-full font-medium hover:bg-card/50 transition-colors text-center"
                >
                  Se hvordan det virker
                </a>
              </div>
            </div>

            <div className="md:col-span-4 grid grid-cols-1 gap-4 self-center">
              {[
                { label: 'Dokumenter / md', value: '4.000+' },
                { label: 'Systemer understøttet', value: 'ERP & mere' },
                { label: 'Gennemsnitlig hastighed', value: '< 3 sek' },
              ]?.map((stat, i) => (
                <div
                  key={i}
                  className={`p-6 border border-border rounded-lg bg-card/50 backdrop-blur-sm reveal delay-${(i + 1) * 100}`}
                >
                  <div className="text-3xl font-serif font-light text-foreground mb-1">{stat?.value}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{stat?.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute top-32 left-8 md:left-12 opacity-30 z-20">
          <span className="font-mono text-xs text-muted-foreground">00 / INTRO</span>
        </div>
      </section>
      {/* Problem Section */}
      <section className="py-24 px-6 md:px-12 border-b border-border relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              01 / PROBLEMET
            </span>
            <h2 className="text-4xl md:text-6xl text-foreground tracking-tight font-serif font-light mb-6">
              Bruger I stadig tid på at håndtere dokumenter manuelt?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: '📄', text: "PDF'er skal læses og tastes ind" },
              { icon: '📊', text: 'Excel filer skal valideres manuelt' },
              { icon: '⚠️', text: 'Data ender med fejl i jeres systemer' },
              { icon: '🔍', text: 'Overblik mangler på tværs af systemer' },
            ]?.map((item, i) => (
              <div
                key={i}
                className={`p-8 border border-border rounded-lg bg-card flex items-start gap-4 reveal delay-${(i + 1) * 100}`}
              >
                <span className="text-3xl">{item?.icon}</span>
                <p className="text-lg text-muted-foreground leading-relaxed">{item?.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 p-8 border border-destructive/30 rounded-lg bg-destructive/5 reveal delay-300">
            <p className="text-xl text-foreground font-serif font-light text-center">
              👉 Det koster tid, penge og skaber fejl
            </p>
          </div>
        </div>
      </section>
      {/* Solution Section */}
      <section className="py-24 px-6 md:px-12 border-b border-border relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              02 / LØSNINGEN
            </span>
            <h2 className="text-4xl md:text-6xl text-foreground tracking-tight font-serif font-light mb-6">
              Én platform til hele jeres dokumentflow
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl">
              Med Dataflowsuite bliver dokumenter automatisk behandlet og valideret – direkte i jeres systemer.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: '🔄',
                title: 'Konverteret til struktureret data',
                desc: 'PDF og Excel omdannes automatisk til ren JSON – klar til brug i jeres systemer.',
              },
              {
                icon: '✅',
                title: 'Valideret direkte i jeres systemer',
                desc: 'Data matches og valideres automatisk i ERP eller andre systemer uden manuel indgriben.',
              },
              {
                icon: '🖥️',
                title: 'Samlet i én portal med fuldt overblik',
                desc: 'Se status, fejl og historik ét sted. Fuld transparens over hele jeres dokumentflow.',
              },
            ]?.map((item, i) => (
              <div
                key={i}
                className={`p-8 border border-border rounded-lg bg-card hover:border-primary/30 transition-colors reveal delay-${(i + 1) * 100}`}
              >
                <div className="text-4xl mb-6">{item?.icon}</div>
                <h3 className="text-xl text-foreground font-medium mb-3">{item?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <div id="how-it-works">
        <HowItWorks />
      </div>
      {/* Sales Copy Sections */}
      <SalesCopy />
      {/* Pricing */}
      <div id="pricing">
        <Pricing />
      </div>
      {/* Value / Benefits */}
      <ValueBenefits />
      {/* Use Cases */}
      <UseCases />
      {/* Trust */}
      <TrustSection />
      {/* CTA Section */}
      <section id="contact" className="py-24 px-6 md:px-12 border-b border-border relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              07 / KOM I GANG
            </span>
            <h2 className="text-4xl md:text-6xl text-foreground tracking-tight font-serif font-light mb-6">
              Klar til at automatisere jeres dokumentflow?
            </h2>
            <p className="text-xl text-muted-foreground mb-4">
              Start simpelt – og skalér efter behov.
            </p>
            <p className="text-muted-foreground/70 mb-12 max-w-2xl mx-auto">
              De fleste starter med vores core løsning og skalerer derefter op i takt med deres dokumentvolumen og integrationsbehov.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center reveal delay-200">
            <a
              href="mailto:kontakt@dataflowsuite.dk"
              className="px-10 py-5 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity text-lg"
            >
              Book demo
            </a>
            <a
              href="mailto:kontakt@dataflowsuite.dk"
              className="px-10 py-5 border border-border text-foreground rounded-full font-medium hover:bg-card/50 transition-colors text-lg"
            >
              Kontakt os
            </a>
          </div>
        </div>
      </section>
      {/* Footer */}
      <Footer />
    </div>
  );
}
